import { PriceUtils, ProductChoice, hasDiscount } from "@repo/common"
import { cn } from "@repo/ui/lib/utils"
import * as React from "react"
import { useTranslations } from "use-intl"
import { OriginalPrice } from "../../components/original-price"
import { ProductOrderingIndicator } from "./product-ordering-indicator"
import {
  SelectableCard,
  SelectableCardBadge,
  SelectableCardContent,
  SelectableCardDescription,
  SelectableCardHeader,
  SelectableCardIndicator,
  SelectableCardLabel,
  SelectableCardSection,
  SelectableCardText,
} from "./selectable-card"

interface ServiceProposalPartActionProductChoicesFieldProps {
  productChoices: ProductChoice[]
  onProductSelect?: (productId: string, choiceId: string) => void
  shouldDimContent?: boolean
  className?: string
  productDecisions?: Record<string, string>
  interactive?: boolean
  showComments?: boolean
}

const ServiceProposalPartActionProductChoicesField = React.forwardRef<
  HTMLDivElement,
  ServiceProposalPartActionProductChoicesFieldProps
>(
  (
    {
      className,
      productChoices,
      onProductSelect,
      shouldDimContent = false,
      productDecisions = {},
      interactive = true,
      showComments = true,
    },
    ref,
  ) => {
    const t = useTranslations("ui")
    if (productChoices.length === 0) return null

    return (
      <SelectableCardSection
        ref={ref}
        className={cn(shouldDimContent ? "opacity-40" : "", className)}
      >
        {productChoices.map((choiceGroup: ProductChoice, index) => {
          const allProducts = [
            choiceGroup.recommendedAlternative,
            ...choiceGroup.otherAlternatives,
          ]
          return (
            <div key={choiceGroup.id} className="space-y-2">
              <SelectableCardLabel>
                {productChoices.length === 1
                  ? t("proposalForm.productChoice")
                  : t("proposalForm.productChoiceNumber", {
                      number: index + 1,
                    })}
              </SelectableCardLabel>
              {showComments && choiceGroup.comment && (
                <SelectableCardDescription>
                  {choiceGroup.comment}
                </SelectableCardDescription>
              )}
              {allProducts
                .sort((a, b) => Number(a.price) - Number(b.price))
                .map((productAlternative) => {
                  // If there's an explicit decision, use that. If not, default to recommended alternative
                  const selectedProductId = productDecisions[choiceGroup.id]
                  const isSelected = selectedProductId
                    ? selectedProductId === productAlternative.id
                    : productAlternative.id ===
                      choiceGroup.recommendedAlternative.id
                  const activeDiscount = hasDiscount(
                    productAlternative.discount,
                  )
                    ? productAlternative.discount
                    : null
                  const discountedPrice = activeDiscount
                    ? PriceUtils.applyDiscount(
                        productAlternative.price,
                        productAlternative.discount ?? null,
                      )
                    : productAlternative.price
                  return (
                    <SelectableCard
                      key={productAlternative.id}
                      selected={isSelected}
                      onSelect={() =>
                        onProductSelect?.(productAlternative.id, choiceGroup.id)
                      }
                      variant="radio"
                      color="secondary"
                      interactive={!shouldDimContent && interactive}
                    >
                      <SelectableCardHeader>
                        <SelectableCardText>
                          {productAlternative.title}
                        </SelectableCardText>
                        <SelectableCardIndicator />
                      </SelectableCardHeader>

                      <SelectableCardContent>
                        {activeDiscount ? (
                          <div className="flex items-center gap-2">
                            <OriginalPrice>
                              {PriceUtils.format(productAlternative.price)}
                            </OriginalPrice>
                            <SelectableCardLabel className="text-destructive-foreground">
                              {PriceUtils.format(discountedPrice)}
                            </SelectableCardLabel>
                            <span className="text-xs text-destructive-foreground">
                              {PriceUtils.formatDiscountLabel(activeDiscount)}
                            </span>
                          </div>
                        ) : (
                          <SelectableCardLabel>
                            {PriceUtils.format(productAlternative.price)}
                          </SelectableCardLabel>
                        )}

                        {productAlternative.id ===
                          choiceGroup.recommendedAlternative.id && (
                          <SelectableCardBadge>
                            {t("readOnlyServiceProposal.recommended")}
                          </SelectableCardBadge>
                        )}
                        {productAlternative.needsOrdering && (
                          <ProductOrderingIndicator
                            expectedDeliveryDate={
                              productAlternative.expectedDeliveryDate
                            }
                          />
                        )}
                      </SelectableCardContent>
                    </SelectableCard>
                  )
                })}
            </div>
          )
        })}
      </SelectableCardSection>
    )
  },
)

ServiceProposalPartActionProductChoicesField.displayName =
  "ServiceProposalPartActionProductChoicesField"

export {
  ServiceProposalPartActionProductChoicesField,
  type ServiceProposalPartActionProductChoicesFieldProps,
}
